import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, timer } from 'rxjs';
import { concatMap, expand } from 'rxjs/operators';
import { EstadoDocumentoEnum } from 'src/app/global/enums/estado-documento.enum';
import { IndicadorEnum } from 'src/app/global/enums/indicador.enum';
import { TipoDocumentoAdjuntoEnum } from 'src/app/global/enums/tipo-documento-adjunto.enum';
import { TipoDocumentoEnum } from 'src/app/global/enums/tipo-documento.enum';
import { DocumentConfig } from 'src/app/global/interfaces/document-config';
import { DocumentosAdjuntosRequest } from 'src/app/global/interfaces/documentos-adjuntos-request';
import { EstadoDocumento } from 'src/app/global/interfaces/estado-documento';
import { GeneralResponseGeneric } from 'src/app/global/interfaces/general-response-generic';
import { DocumentService } from 'src/app/global/services/document.service';
import { DocumentVMService } from 'src/app/view-model/services/document-vm.service';
import { FileConverter } from 'src/app/view-model/utils/files/file-converter';
import { fileExtensionValidator } from 'src/app/view-model/validators/file-extension.validator';
import { fileSizeValidator } from 'src/app/view-model/validators/file-size.validator';
import { environment } from 'src/environments/environment';

@Injectable()
export class DocumentVMProvider extends DocumentVMService {

  private obtenerEstadoDocumentoDelay = environment.documentosAdjuntos.obtenerEstadoDocumentoDelay;

  private acceptedFormats = [
    'jpg',
    'jpeg',
    'png',
    'pdf'
  ];
  private prefFormats = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf'
  ];
  private fourBytes = 4 * 1024 * 1024;
  private eightBytes = 8 * 1024 * 1024;

  documentConfig: DocumentConfig = {
    BOLETA_O_FACTURA: {
      maxFileSizeInBytes: this.fourBytes,
      acceptedFormats: this.acceptedFormats,
      show: false,
      isLoaded: false,
      isLoading: false,
      mb: 4
    },
    FOTOGRAFIA_CEDULA: {
      maxFileSizeInBytes: this.fourBytes,
      acceptedFormats: this.acceptedFormats,
      show: false,
      isLoaded: false,
      isLoading: false,
      mb: 4
    },
    FERIAS_Y_EVENTOS: {
      maxFileSizeInBytes: this.fourBytes,
      acceptedFormats: this.acceptedFormats,
      show: false,
      isLoaded: false,
      isLoading: false,
      mb: 4
    },
    PATENTE_COMERCIAL: {
      maxFileSizeInBytes: this.fourBytes,
      acceptedFormats: this.acceptedFormats,
      show: false,
      isLoaded: false,
      isLoading: false,
      mb: 4
    },
    ESCRITURA: {
      maxFileSizeInBytes: this.eightBytes,
      acceptedFormats: this.acceptedFormats,
      show: false,
      isLoaded: false,
      isLoading: false,
      mb: 8
    }
  };

  constructor(private documentService: DocumentService) {
    super();
  }

  createForm(): FormGroup {
    return new FormGroup({
      BOLETA_O_FACTURA: new FormControl(null, [
        Validators.required,
        fileExtensionValidator(this.documentConfig.BOLETA_O_FACTURA.acceptedFormats),
        fileSizeValidator(this.documentConfig.BOLETA_O_FACTURA.maxFileSizeInBytes)
      ]),
      FOTOGRAFIA_CEDULA: new FormControl(null, [
        Validators.required,
        fileExtensionValidator(this.documentConfig.FOTOGRAFIA_CEDULA.acceptedFormats),
        fileSizeValidator(this.documentConfig.FOTOGRAFIA_CEDULA.maxFileSizeInBytes)
      ]),
      FERIAS_Y_EVENTOS: new FormControl(null, [
        Validators.required,
        fileExtensionValidator(this.documentConfig.FERIAS_Y_EVENTOS.acceptedFormats),
        fileSizeValidator(this.documentConfig.FERIAS_Y_EVENTOS.maxFileSizeInBytes)
      ]),
      PATENTE_COMERCIAL: new FormControl(null, [
        Validators.required,
        fileExtensionValidator(this.documentConfig.PATENTE_COMERCIAL.acceptedFormats),
        fileSizeValidator(this.documentConfig.PATENTE_COMERCIAL.maxFileSizeInBytes)
      ]),
      ESCRITURA: new FormControl(null, [
        Validators.required,
        fileExtensionValidator(this.documentConfig.ESCRITURA.acceptedFormats),
        fileSizeValidator(this.documentConfig.ESCRITURA.maxFileSizeInBytes)
      ])
    });
  }

  documentsConfig(): DocumentConfig {
    return this.documentConfig;
  }

  async getDocumentoRequeridos(form: FormGroup) {
    const response = await this.documentService.requiredDocuments().toPromise();
    if (response && response.length > 0) {
      response.forEach(doc => {
        this.documentConfig[doc.value0].show = true;

      });
    }
    this.setValidators(form);
    return response;
  }

  private setValidators(form: FormGroup) {
    if (!this.documentConfig.BOLETA_O_FACTURA.show) {
      form.get('BOLETA_O_FACTURA').disable();
    }
    if (!this.documentConfig.FOTOGRAFIA_CEDULA.show) {
      form.get('FOTOGRAFIA_CEDULA').disable();
    }
    if (!this.documentConfig.FERIAS_Y_EVENTOS.show) {
      form.get('FERIAS_Y_EVENTOS').disable();
    }
    if (!this.documentConfig.PATENTE_COMERCIAL.show) {
      form.get('PATENTE_COMERCIAL').disable();
    }
    if (!this.documentConfig.ESCRITURA.show) {
      form.get('ESCRITURA').disable();
    }
  }

  replaceBase64(base64: string) {
    const word = 'data:{{f}};base64,';
    let result = '';
    this.prefFormats.forEach((format) => {
      const auxWord = word.replace('{{f}}', format);
      if (base64.includes(auxWord)) {
        result = base64.replace(auxWord, '');
      }
    });
    return result;
  }

  async saveDocuments(form: FormGroup, validForm: boolean, documentConfig: DocumentConfig): Promise<GeneralResponseGeneric<unknown, IndicadorEnum>> {
    if (!validForm) {
      throw new Error('invalid form');
    }
    const converter = new FileConverter();

    const request: DocumentosAdjuntosRequest = {
      documentos: []
    };

    if (this.documentConfig.BOLETA_O_FACTURA.show) {
      const value = form.get('BOLETA_O_FACTURA').value;
      if (value) {
        const base64 = await converter.fileToBase64(value);
        request.documentos.push({
          tipo: TipoDocumentoAdjuntoEnum.BOLETA_O_FACTURA,
          contenido: this.replaceBase64(base64)
        });
      }
    }
    if (this.documentConfig.FOTOGRAFIA_CEDULA.show) {
      const value = form.get('FOTOGRAFIA_CEDULA').value;
      if (value) {
        const base64 = await converter.fileToBase64(value);
        request.documentos.push({
          tipo: TipoDocumentoAdjuntoEnum.FOTOGRAFIA_CEDULA,
          contenido: this.replaceBase64(base64)
        });
      }
    }
    if (this.documentConfig.FERIAS_Y_EVENTOS.show) {
      const value = form.get('FERIAS_Y_EVENTOS').value;
      if (value) {
        const base64 = await converter.fileToBase64(value);
        request.documentos.push({
          tipo: TipoDocumentoAdjuntoEnum.FERIAS_Y_EVENTOS,
          contenido: this.replaceBase64(base64)
        });
      }
    }
    if (this.documentConfig.PATENTE_COMERCIAL.show) {
      const value = form.get('PATENTE_COMERCIAL').value;
      if (value) {
        const base64 = await converter.fileToBase64(value);
        request.documentos.push({
          tipo: TipoDocumentoAdjuntoEnum.PATENTE_COMERCIAL,
          contenido: this.replaceBase64(base64)
        });
      }
    }
    if (this.documentConfig.ESCRITURA.show) {
      const value = form.get('ESCRITURA').value;
      if (value) {
        const base64 = await converter.fileToBase64(value);
        request.documentos.push({
          tipo: TipoDocumentoAdjuntoEnum.ESCRITURA,
          contenido: this.replaceBase64(base64)
        });
      }
    }

    const response = await this.documentService.saveDocument(request).toPromise();
    return response;

  }

  // /**
  //  * muestra y oculta los inputs de carga dependiendo el rubro
  //  */
  // validarRubros(rubro: IRubro) {


  // }

  async getDocumentStatus() {
    const status: any[] = await this.documentService.documentStatus().toPromise();
    // status.forEach((doc) => {
    //   if(doc.estado === 'GUARDADO') {
    //     this.documentConfig[doc.tipoDocumento].isLoaded = true;
    //   }
    // });
    return status;
  }

  startDocumentStatusRequester(): Observable<EstadoDocumento[]> {
    return this.documentService.documentStatus().pipe(
      expand(() => timer(this.obtenerEstadoDocumentoDelay).pipe(
        concatMap(() => this.documentService.documentStatus())
      )
      )
    );
  }

  updateDocumentStatus(documentStatusList: EstadoDocumento[], documentsConfig: DocumentConfig, form: FormGroup): void {
    if (!documentStatusList) { return; }
    for (const documentStatus of documentStatusList) {
      const isDocumentLoaded = this.isDocumentLoaded(documentStatus);
      switch (documentStatus.tipoDocumento) {
        case TipoDocumentoEnum.CEDULA_IDENTIDAD:
          if (isDocumentLoaded) {
            documentsConfig.FOTOGRAFIA_CEDULA.isLoaded = true;
          }
          break;
        case TipoDocumentoEnum.PATENTE_COMERCIAL:
          if (isDocumentLoaded) {
            documentsConfig.PATENTE_COMERCIAL.isLoaded = true;
          }
          break;
        case TipoDocumentoEnum.ESCRITURA:
          if (isDocumentLoaded) {
            documentsConfig.ESCRITURA.isLoaded = true;
          }
          break;
        case TipoDocumentoEnum.EVIDENCIA_VENTAS:
          if (isDocumentLoaded) {
            documentsConfig.BOLETA_O_FACTURA.isLoaded = true;
          }
          break;
        case TipoDocumentoEnum.EVIDENCIA_FERIA_EVENTO:
          if (isDocumentLoaded) {
            documentsConfig.FERIAS_Y_EVENTOS.isLoaded = true;
          }
          break;
      }
    }
  }

  isDocumentLoaded(documentStatus: EstadoDocumento): boolean {
    let isLoaded = false;
    if (documentStatus.estado === EstadoDocumentoEnum.GUARDADO) {
      isLoaded = true;
    }
    return isLoaded;
  }

  stopDocumentStatusRequester(): void {
    // this.killDocumentStatusTrigger.next();
    return;
  }

  setVisivility(show: boolean) {
    this.documentConfig.BOLETA_O_FACTURA.show = show;
    this.documentConfig.FOTOGRAFIA_CEDULA.show = show;
    this.documentConfig.FERIAS_Y_EVENTOS.show = show;
    this.documentConfig.PATENTE_COMERCIAL.show = show;
    this.documentConfig.ESCRITURA.show = show;
  }

  deleteDocuments(): Promise<any> {
    return this.documentService.deleteDocument().toPromise();
  }

}

interface IRubro {
  id: number;
  nombre: string;
  esRiesgoso: boolean;
  esGenerico: boolean;
  prevencionDelitos: boolean;
}
